import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Plus, Trash2 } from "lucide-react";

const ZONES = [
  { value: "entrada", label: "Entrada" },
  { value: "barra", label: "Barra" },
  { value: "comedor", label: "Comedor" },
];

const TEMPLATE_OPTIONS = [
  {
    key: "stock",
    title: "Falta stock",
    content: "Falta stock de [producto] en [zona]. Reponer urgente.",
  },
  {
    key: "limpieza",
    title: "Limpieza",
    content: "Zona [zona] necesita limpieza.",
  },
  {
    key: "incidencia",
    title: "Incidencia",
    content: "Incidencia en [zona]: [descripción].",
  },
  {
    key: "tecnico",
    title: "Técnico",
    content: "Problema técnico en [zona]: [detalle].",
  },
  {
    key: "urgente",
    title: "Urgente",
    content: "Atención urgente en [zona].",
  },
  {
    key: "general",
    title: "Aviso general",
    content: "[mensaje libre]",
  },
];

const TEMPLATE_MAP = Object.fromEntries(TEMPLATE_OPTIONS.map((template) => [template.key, template]));

const buildTemplateContent = (templateKey, zone, product) => {
  const template = TEMPLATE_MAP[templateKey];
  const zoneLabel = ZONES.find((item) => item.value === zone)?.label ?? zone;
  if (!template) return "";
  return template.content
    .replaceAll("[zona]", zoneLabel)
    .replaceAll("[producto]", product || "[producto]");
};

const buildFormFromTemplate = (templateKey, zone, product) => {
  const template = TEMPLATE_MAP[templateKey];
  return {
    template_key: templateKey,
    title: template?.title ?? "",
    content: buildTemplateContent(templateKey, zone, product),
  };
};

const extractZoneLabel = (content = "") => {
  const normalized = content.toLowerCase();
  const match = ZONES.find((zone) => normalized.includes(zone.label.toLowerCase()));
  return match?.label ?? null;
};

export default function AdminMensajes() {
  const [messages, setMessages] = useState([]);
  const [zone, setZone] = useState(ZONES[0].value);
  const [product, setProduct] = useState("");
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState(() => buildFormFromTemplate(TEMPLATE_OPTIONS[0].key, ZONES[0].value, ""));
  const copiedTimeoutRef = useRef(null);

  const loadMsgs = () => api.get("/internal-messages").then((r) => setMessages(r.data));

  useEffect(() => {
    loadMsgs();
  }, []);

  useEffect(() => () => {
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
  }, []);

  const showCopiedFeedback = () => {
    setCopied(true);
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      copiedTimeoutRef.current = null;
    }, 2000);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopiedFeedback();
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const selectTemplate = async (templateKey, nextZone = zone, nextProduct = product) => {
    const nextForm = buildFormFromTemplate(templateKey, nextZone, nextProduct);
    setForm(nextForm);
    await copy(nextForm.content);
  };

  const handleZoneChange = (nextZone) => {
    setZone(nextZone);
    setForm((current) => buildFormFromTemplate(current.template_key, nextZone, product));
  };

  const handleProductChange = (nextProduct) => {
    setProduct(nextProduct);
    setForm((current) => buildFormFromTemplate(current.template_key, zone, nextProduct));
  };

  const create = async () => {
    if (!form.title || !form.content) return toast.error("Completa título y contenido");
    try {
      await api.post("/internal-messages", form);
      toast.success("Mensaje guardado");
      loadMsgs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar mensaje?")) return;
    await api.delete(`/internal-messages/${id}`);
    loadMsgs();
  };

  const sendWhatsApp = () => {
    const text = form.content.trim();
    if (!text) return;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6" data-testid="admin-mensajes">
      <header>
        <h1 className="font-display font-black text-3xl">Mensajes internos</h1>
        <p className="text-muted-foreground">Avisos rápidos para el staff. Copia y pégalo donde quieras (futura integración con WhatsApp).</p>
      </header>

      <div className="bg-white rounded-2xl border border-border p-5 space-y-3" data-testid="msg-create">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-bold mb-1" htmlFor="msg-zone">Zona</label>
            <select
              id="msg-zone"
              value={zone}
              onChange={(e) => handleZoneChange(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2"
              data-testid="msg-zone"
            >
              {ZONES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1" htmlFor="msg-product">Producto</label>
            <input
              id="msg-product"
              placeholder="Ej. hielo, cerveza, vasos"
              value={product}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full rounded-lg border border-border px-3 py-2"
              data-testid="msg-product"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold mb-1">Plantilla</label>
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_OPTIONS.map((template) => (
              <button
                type="button"
                key={template.key}
                onClick={() => selectTemplate(template.key)}
                data-testid={`template-${template.key}`}
                className={`px-3 py-1.5 text-sm rounded-full border transition ${form.template_key === template.key ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"}`}
              >
                {template.title}
              </button>
            ))}
          </div>
          <div className="mt-1 h-5 text-xs text-muted-foreground" data-testid="msg-copied-feedback">
            {copied ? "Copiado ✔" : ""}
          </div>
        </div>

        <input
          placeholder="Título"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full rounded-lg border border-border px-3 py-2"
          data-testid="msg-title"
        />
        <textarea
          rows={3}
          placeholder="Contenido"
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          className="w-full rounded-lg border border-border px-3 py-2"
          data-testid="msg-content"
        />
        <div className="flex gap-2">
          <button onClick={() => copy(form.content)} className="inline-flex items-center gap-2 bg-accent px-4 py-2 rounded-full font-medium" data-testid="msg-copy-current">
            <Copy size={14} /> Copiar borrador
          </button>
          <button onClick={sendWhatsApp} className="inline-flex items-center gap-2 bg-accent px-4 py-2 rounded-full font-medium" data-testid="msg-whatsapp-current">
            Enviar por WhatsApp
          </button>
          <button onClick={create} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold" data-testid="msg-save-btn">
            <Plus size={14} /> Guardar aviso
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="font-display font-bold">Historial</h2>
        {messages.length === 0 && <div className="text-muted-foreground text-sm">Aún no has creado mensajes internos.</div>}
        {messages.map((m) => {
          const zoneLabel = extractZoneLabel(m.content);
          return (
            <div key={m.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`msg-row-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold">{m.title}</div>
                  <div className="text-sm whitespace-pre-line mt-1">
                    {zoneLabel && (
                      <span className="inline-flex items-center rounded-full bg-accent px-2 py-0.5 mr-2 font-bold text-foreground">
                        [{zoneLabel}]
                      </span>
                    )}
                    {m.content}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-2">{new Date(m.created_at).toLocaleString("es-ES")}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => copy(m.content)} className="h-9 w-9 grid place-items-center rounded-lg bg-accent" data-testid={`msg-copy-${m.id}`}><Copy size={14} /></button>
                  <button onClick={() => remove(m.id)} className="h-9 w-9 grid place-items-center rounded-lg bg-destructive text-destructive-foreground" data-testid={`msg-delete-${m.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
